import React, { useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";
import { AnimatePresence, motion } from "framer-motion";

import {
  DISMISS_TTL_MS,
  INTL_SITE_URL,
  STORAGE_KEY,
} from "../../lib/geoBanner/config";
import { isDismissed, setDismissed } from "../../lib/geoBanner/storage";
import {
  shouldShowGeoBanner,
  stripBypassQueryParam,
  hasBypassQueryParam,
  getClientRouteContext,
} from "../../lib/geoBanner/utils";
import {
  StyledGeoBannerActions,
  StyledGeoBannerCta,
  StyledGeoBannerDismiss,
  StyledGeoContent,
  StyledGeoCopy,
  StyledGeoModal,
  StyledGeoOverlay,
} from "./styles";

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M3 12H21M12 3C9.5 6 8.5 9 8.5 12C8.5 15 9.5 18 12 21M12 3C14.5 6 15.5 9 15.5 12C15.5 15 14.5 18 12 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6L18 18M18 6L6 18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

function resolveVisibility(pathname, search) {
  if (hasBypassQueryParam(search)) return false;

  return (
    shouldShowGeoBanner({ pathname, search }) &&
    !isDismissed(STORAGE_KEY, DISMISS_TTL_MS)
  );
}

function getRouteContext(router) {
  if (router.isReady) {
    return {
      pathname: router.asPath.split("?")[0],
      search:
        router.asPath.split("?")[1] ||
        (typeof window !== "undefined" ? window.location.search.slice(1) : ""),
    };
  }

  return getClientRouteContext();
}

function getPortalRoot() {
  if (typeof document === "undefined") return null;

  return document.getElementById("portal") || document.body;
}

/**
 * Geo suggestion modal for non-Italian visitors.
 *
 * Local testing:
 * - Non-IT visitor:  http://localhost:3000/?cf_country=DE
 * - Italian visitor: http://localhost:3000/?cf_country=IT
 * - Back from intl:  http://localhost:3000/?from_intl=true
 * - Excluded path:   http://localhost:3000/case-studies/foo?cf_country=DE
 * - Manual cookie:   document.cookie = "cf_country=DE; path=/"
 */
const GeoBanner = () => {
  const router = useRouter();
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setDismissed(STORAGE_KEY);
    setVisible(false);
  }, []);

  const handleCtaClick = useCallback(() => {
    setDismissed(STORAGE_KEY);
    setVisible(false);
  }, []);

  const handleOverlayClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        dismiss();
      }
    },
    [dismiss]
  );

  useLayoutEffect(() => {
    const { pathname, search } = getRouteContext(router);

    if (hasBypassQueryParam(search)) {
      stripBypassQueryParam();
      setVisible(false);
      return;
    }

    setVisible(resolveVisibility(pathname, search));
  }, [router.isReady, router.asPath]);

  useLayoutEffect(() => {
    if (!visible) return;

    const onEscPress = (event) => {
      if (event.key === "Escape") dismiss();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onEscPress);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onEscPress);
    };
  }, [visible, dismiss]);

  return getPortalRoot()
    ? createPortal(
        <AnimatePresence>
          {visible && (
            <StyledGeoOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              onClick={handleOverlayClick}
              data-geo-banner-overlay
            >
              <StyledGeoModal
                as={motion.div}
                role="dialog"
                aria-modal="true"
                aria-labelledby="geo-banner-title"
                aria-describedby="geo-banner-description"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                onClick={(event) => event.stopPropagation()}
                data-geo-banner
              >
                <StyledGeoContent>
                  <StyledGeoCopy id="geo-banner-description">
                    <strong id="geo-banner-title">
                      Visiting from outside Italy?
                    </strong>
                    <br />
                    <br />
                    We have a dedicated space for international visitors,
                    curated to reflect a wider world perspective.
                  </StyledGeoCopy>

                  <StyledGeoBannerActions>
                    <StyledGeoBannerCta
                      href={INTL_SITE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleCtaClick}
                    >
                      <GlobeIcon />
                      Go to Caffeina International
                    </StyledGeoBannerCta>
                    <StyledGeoBannerDismiss type="button" onClick={dismiss}>
                      <CloseIcon />
                      Stay here
                    </StyledGeoBannerDismiss>
                  </StyledGeoBannerActions>
                </StyledGeoContent>
              </StyledGeoModal>
            </StyledGeoOverlay>
          )}
        </AnimatePresence>,
        getPortalRoot()
      )
    : null;
};

export default GeoBanner;
